import React, { useState, useEffect } from 'react';
import { Play, Pause, RotateCcw, ChevronRight, ChevronLeft, Film, Clapperboard, Tv, Camera, Clock } from 'lucide-react';

const AnimationMuseum = () => {
  const [activeGallery, setActiveGallery] = useState('classics');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentExhibit, setCurrentExhibit] = useState(0);
  const [frameCount, setFrameCount] = useState(0);

  const exhibits = [
    { 
      title: "Pardon Our Dust!",
      creator: "- Necrova Industries -",
      technique: "Toon Tales Museum Coming Soon!",
      color: "#FF4500", // Bright orange-red
      background: "#FFFF00", // Bright yellow
      description: "COMING SOON"
    },
  ];

  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setFrameCount(prev => prev + 1);
      }, 150);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  const nextExhibit = () => {
    setCurrentExhibit((currentExhibit + 1) % exhibits.length);
  };

  const prevExhibit = () => {
    setCurrentExhibit((currentExhibit - 1 + exhibits.length) % exhibits.length);
  };

  // Bouncing animation for elements
  const bouncingStyle = {
    animation: isPlaying ? 'bounce 0.5s infinite alternate' : 'none',
  };

  return (
    <div className="flex flex-col min-h-screen bg-blue-200 text-gray-800">
      {/* Custom CSS for cartoon effects */}
      <style jsx>{`
        @keyframes bounce {
          from { transform: translateY(0); }
          to { transform: translateY(-10px); }
        }
        
        @keyframes wiggle {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(-5deg); }
          75% { transform: rotate(5deg); }
          100% { transform: rotate(0deg); }
        }
        
        .cartoon-border {
          border: 4px solid black;
          border-radius: 24px;
          box-shadow: 8px 8px 0px #000;
        }
        
        .cartoon-button {
          border: 3px solid black;
          box-shadow: 4px 4px 0px #000;
          transition: all 0.1s ease;
        }
        
        .cartoon-button:hover {
          transform: translate(-2px, -2px);
          box-shadow: 6px 6px 0px #000;
        }
        
        .cartoon-button:active {
          transform: translate(2px, 2px);
          box-shadow: 2px 2px 0px #000;
        }
        
        .text-shadow {
          text-shadow: 2px 2px 0px #000;
        }
        
        .wobble {
          animation: wiggle 2s infinite;
        }
      `}</style>
      
      {/* Header */}
      <header className="bg-yellow-400 text-blue-800 p-4 border-b-4 border-black">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Film className="text-red-500 wobble" size={32} />
            <h1 className="text-3xl font-bold text-shadow" style={{fontFamily: 'Comic Sans MS, cursive'}}>TOON TALES MUSEUM!</h1>
          </div>
          <div className="hidden md:flex items-center space-x-1 font-bold">
            <Clock size={18} className="mr-1 text-red-500" />
            <span className="text-sm">Open: 9AM - 6PM</span>
            <span className="mx-3">|</span>
            <Camera size={18} className="mr-1 text-red-500" />
            <span className="text-sm">Times Subject To Change</span>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-red-500 p-2 sticky top-0 z-10 border-b-4 border-black">
        <div className="max-w-4xl mx-auto flex justify-between">
          <div className="flex space-x-1">
            <button 
              className={`px-4 py-2 rounded-lg font-bold cartoon-button ${activeGallery === 'classics' ? 'bg-yellow-300 text-blue-800' : 'bg-blue-300 text-blue-800 hover:bg-yellow-300'}`}
              onClick={() => setActiveGallery('classics')}
            >
              Museum Exhibits
            </button>
            <button 
              className={`px-4 py-2 rounded-lg font-bold cartoon-button ${activeGallery === 'techniques' ? 'bg-yellow-300 text-blue-800' : 'bg-blue-300 text-blue-800 hover:bg-yellow-300'}`}
              onClick={() => setActiveGallery('techniques')}
            >
             Meet Our Staff
            </button>
            <button 
              className={`px-4 py-2 rounded-lg font-bold cartoon-button ${activeGallery === 'workshop' ? 'bg-yellow-300 text-blue-800' : 'bg-blue-300 text-blue-800 hover:bg-yellow-300'}`}
              onClick={() => setActiveGallery('workshop')}
            >
              Workshop
            </button>
          </div>
          <button className="px-4 py-2 bg-green-400 rounded-lg font-bold cartoon-button text-blue-800 hover:bg-green-300">
            GET TICKETS!
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow p-6 bg-blue-200">
        <div className="max-w-4xl mx-auto">
          {activeGallery === 'classics' && (
            <div className="flex flex-col items-center">
              <h2 className="text-4xl font-bold mb-6 text-shadow text-red-500" style={{fontFamily: 'Comic Sans MS, cursive'}}>PARDON OUR DUST!</h2>
              <p className="text-center mb-8 text-blue-800 font-bold max-w-2xl text-xl">
                We're sweeping up and building our AMAZING website right now! Check back soon for a SPECTACULAR experience!
              </p>
              
              <div className="w-full bg-yellow-300 rounded-3xl overflow-hidden cartoon-border">
                {/* Exhibit Display */}
                <div className="relative h-72 overflow-hidden" style={{ backgroundColor: exhibits[currentExhibit].background }}>
                  {/* Animation cells effect */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    {[...Array(5)].map((_, i) => (
                      <div 
                        key={i} 
                        className="absolute border-4 border-black bg-white/20 rounded-xl"
                        style={{
                          width: `${300 - i * 20}px`,
                          height: `${200 - i * 15}px`,
                          transform: `translateX(${(i % 2 === 0 ? 1 : -1) * (frameCount % 10)}px) rotate(${(i % 2 === 0 ? 1 : -1) * 2}deg)`,
                          opacity: 0.7 - i * 0.1
                        }}
                      />
                    ))}
                  </div>
                  
                  {/* Main exhibit animation */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div 
                      className="text-center p-4 transition-all duration-500 ease-in-out"
                      style={{ transform: isPlaying ? `translateY(${frameCount % 10 - 5}px) rotate(${frameCount % 4 - 2}deg)` : 'none' }}
                    >
                      <h3 
                        className="text-5xl font-bold mb-2 text-shadow"
                        style={{ color: exhibits[currentExhibit].color, fontFamily: 'Comic Sans MS, cursive' }}
                      >
                        {exhibits[currentExhibit].title}
                      </h3>
                      <div className="w-16 h-2 mx-auto my-4 bg-black"></div>
                      <p className="text-2xl font-bold text-blue-800"> {exhibits[currentExhibit].creator}</p>
                    </div>
                  </div>
                </div>
                
                {/* Exhibit Info */}
                <div className="p-6 bg-blue-300 border-t-4 border-black">
                  <div className="mb-4">
                    <h4 className="text-2xl font-bold mb-1 text-red-500">Name</h4>
                    <p className="text-blue-800 font-bold text-xl">{exhibits[currentExhibit].technique}</p>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="text-2xl font-bold mb-1 text-red-500">Description</h4>
                    <p className="text-blue-800 font-bold text-xl">{exhibits[currentExhibit].description}</p>
                  </div>
                  
                  <div className="flex justify-center space-x-2 mb-2">
                    <div className="flex items-center space-x-2 text-lg font-bold text-blue-800">
                      <Film size={20} className="text-red-500" />
                      <span>Exhibit {currentExhibit + 1} of {exhibits.length}</span>
                    </div>
                  </div>
                  
                  {/* Controls */}
                  <div className="flex justify-between items-center pt-4 border-t-4 border-black">
                    <div>
                      <button 
                        className="p-3 bg-red-500 text-white rounded-full hover:bg-red-400 border-2 border-black"
                        onClick={() => setIsPlaying(!isPlaying)}
                        aria-label={isPlaying ? "Pause animation" : "Play animation"}
                      >
                        {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                      </button>
                      <button 
                        className="p-3 bg-green-400 text-blue-800 rounded-full ml-2 hover:bg-green-300 border-2 border-black"
                        onClick={() => setFrameCount(0)}
                        aria-label="Reset animation"
                      >
                        <RotateCcw size={20} />
                      </button>
                    </div>
                    
                    <div className="flex items-center">
                      <button 
                        className="p-3 bg-yellow-400 rounded-l-lg hover:bg-yellow-300 border-2 border-black"
                        onClick={prevExhibit}
                        aria-label="Previous exhibit"
                      >
                        <ChevronLeft size={22} className="text-blue-800" />
                      </button>
                      <button 
                        className="p-3 bg-yellow-400 rounded-r-lg hover:bg-yellow-300 border-2 border-black"
                        onClick={nextExhibit}
                        aria-label="Next exhibit"
                      >
                        <ChevronRight size={22} className="text-blue-800" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeGallery === 'techniques' && (
            <div className="text-center">
              <h2 className="text-4xl font-bold mb-6 text-shadow text-red-500" style={{fontFamily: 'Comic Sans MS, cursive'}}>MEET OUR STAFF!</h2>
              <div className="flex justify-center mb-8">
                <Clapperboard size={72} className="text-yellow-400 wobble" />
              </div>
              <div className="bg-yellow-300 p-8 rounded-3xl cartoon-border">
                <p className="text-2xl font-bold mb-6 text-blue-800">We're assembling our AMAZING crew!</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 border-4 border-black rounded-lg bg-green-300 cartoon-border">
                    <h3 className="text-2xl font-bold mb-2 text-red-500">COMING SOON!</h3>
                    <p className="text-lg font-bold text-blue-800">Our INCREDIBLE staff is still being drawn! Check back soon for the full cast!</p>
                  </div>
                </div>
                <p className="mt-6 text-xl font-bold text-red-500" style={bouncingStyle}>Stay Toon-ed!</p>
              </div>
            </div>
          )}
          
          {activeGallery === 'workshop' && (
            <div className="text-center">
              <h2 className="text-4xl font-bold mb-6 text-shadow text-red-500" style={{fontFamily: 'Comic Sans MS, cursive'}}>WORKSHOP WONDERLAND!</h2>
              <div className="flex justify-center mb-8">
                <Tv size={72} className="text-yellow-400 wobble" />
              </div>
              <div className="bg-yellow-300 p-8 rounded-3xl cartoon-border">
                {/* Workshop Description */}
                <div className="mb-8">
                  <h3 className="text-3xl font-bold mb-4 text-blue-800">PARDON OUR DUST!</h3>
                  <p className="text-xl font-bold mb-6 text-red-500">
                    Our SPECTACULAR workshop is being animated right now! Check back soon!
                  </p>
                </div>

                {/* YouTube Video Embed */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold mb-3 text-blue-800">Workshop Preview</h3>
                  <div className="aspect-w-16 aspect-h-9 cartoon-border">
                    <iframe 
                      className="w-full h-64 md:h-96 rounded-lg"
                      src="https://www.youtube.com/embed/52Z1EIuHPvU?si=HIrPpYoXWR_Nszu4" 
                      title="Workshop Preview"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen
                    ></iframe>
                  </div>
                  <p className="text-lg mt-2 font-bold text-blue-800">
                    Get a glimpse of our workshop and see what our AMAZING tours offer!
                  </p>
                </div>

                {/* Workshop Schedule */}
                <div className="p-6 border-4 border-dashed border-black rounded-lg mb-6 bg-green-300">
                  <h3 className="text-2xl font-bold mb-2 text-red-500">TOUR SCHEDULE</h3>
                  <ul className="text-left space-y-2">
                    <li className="flex justify-between text-xl font-bold text-blue-800">
                      <span>10:00 AM</span>
                    </li>
                    <li className="flex justify-between text-xl font-bold text-blue-800">
                      <span>12:00 PM</span>
                    </li>
                    <li className="flex justify-between text-xl font-bold text-blue-800">
                      <span>2:00 PM</span>
                    </li>
                    <li className="flex justify-between text-xl font-bold text-blue-800">
                      <span>4:00 PM</span>
                    </li>
                  </ul>
                </div>
                <button className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-400 font-bold text-xl cartoon-button">
                  SIGN UP NOW!
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      
      {/* Footer */}
      <footer className="bg-yellow-400 text-blue-800 p-6 border-t-4 border-black">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <h3 className="text-2xl font-bold mb-2" style={{fontFamily: 'Comic Sans MS, cursive'}}>Toon tales</h3>
              <p className="text-red-500 font-bold">Coming to a screen near you!</p>
            </div>
            <div className="flex space-x-4">
              <button className="p-2 bg-blue-300 rounded-full hover:bg-blue-200 border-2 border-black">
                <Film size={24} className="text-red-500" />
              </button>
              <button className="p-2 bg-blue-300 rounded-full hover:bg-blue-200 border-2 border-black">
                <Camera size={24} className="text-red-500" />
              </button>
              <button className="p-2 bg-blue-300 rounded-full hover:bg-blue-200 border-2 border-black">
                <Clapperboard size={24} className="text-red-500" />
              </button>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t-2 border-black text-center">
            <p className="font-bold text-lg"> © 2025 Necrova LLC | That's All Folks! </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AnimationMuseum;